import React, {Fragment, useState} from 'react';
import {Menu, Transition} from "@headlessui/react";
import {EllipsisVerticalIcon} from "@heroicons/react/24/outline";
import {IAccount} from "../../model/IAccount";
import {classNames} from "../../utils/common";
import {accountApi} from "../../api/AccountsService";
import EditAccountForm from "./EditAccountForm";

interface AccountItemProps {
    account: IAccount;
    highlighted?: boolean;
    onHighlight?: (accountId: number) => void;
}

const AccountItem: React.FunctionComponent<AccountItemProps> = ({account, highlighted = false, onHighlight}) => {
    const [editMode, setEditMode] = useState(false);
    const [setAccountStatus, {isLoading}] = accountApi.useSetAccountStatusMutation();
    const [recalculateAccountBalance, {isLoading: isRecalculating}] = accountApi.useRecalculateAccountBalanceMutation();

    async function handleSetAccountStatus() {
        const nextIsActive = !account.is_active;
        try {
            await setAccountStatus({id: account.id, is_active: nextIsActive}).unwrap();
            onHighlight?.(account.id);
        } catch (error) {
            console.log(error);
        }
    }

    function toggleEditMode() {
        setEditMode(!editMode);
    }

    async function handleRecalculateBalance() {
        await recalculateAccountBalance(account.id);
    }

    const highlightClass = highlighted ? 'account-row-highlight' : '';

    return (
        <>
            <Transition
                show={!editMode}
                as="tr"
                enter="ease-in-out duration-300 transform"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
                data-account-id={account.id}
                tabIndex={-1}
                className={classNames(
                    editMode ? "absolute" : "",
                    "account-row-focus",
                    highlightClass
                )}
            >
                <td className="px-3 py-1.5 whitespace-nowrap">
                    <div className="flex items-center">
                        <div className="flex-shrink-0 h-7 w-7">
                            <img className="h-7 w-7 rounded-full" src={`${process.env.PUBLIC_URL}/images/acc_logos/` + account.logo} alt=""/>
                        </div>
                        <div className="ml-2.5">
                            <div className="text-xs font-medium text-gray-900">
                                {account.name}
                            </div>
                        </div>
                    </div>
                </td>
                <td className="px-3 py-1.5 whitespace-nowrap text-xs text-gray-900">
                    {account.currency.code}
                </td>
                <td className="px-3 py-1.5 whitespace-nowrap">
                    <span className="px-1.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        {account.currency.symbol} {account.initial_balance}
                    </span>
                </td>
                <td className="px-3 py-1.5 whitespace-nowrap">
                    <span className="px-1.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                        {account.currency.symbol} {account.balance}
                    </span>
                </td>
                <td className="px-3 py-1.5 whitespace-nowrap">
                    <span className={classNames(
                        account.is_active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800",
                        "px-1.5 inline-flex text-xs leading-5 font-semibold rounded-full")}
                    >
                        {account.is_active ? 'Active' : 'Disabled'}
                    </span>
                </td>
                <td className="px-3 py-1.5 whitespace-nowrap text-right text-xs font-medium">
                    <button className="cursor-pointer text-red-600 hover:text-red-900"
                            disabled={isLoading}
                            onClick={handleSetAccountStatus}
                    >
                        {account.is_active ? 'Disable' : 'Enable'}
                    </button>
                </td>
                <td className="px-3 py-1.5 whitespace-nowrap text-right text-xs font-medium">
                    <Menu as="div" className="relative inline-block text-left">
                        <Menu.Button className="inline-flex items-center justify-center rounded p-0.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800">
                            <EllipsisVerticalIcon className="h-4 w-4"/>
                        </Menu.Button>
                        <Transition
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                        >
                            <Menu.Items className="absolute right-0 z-20 mt-1 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                <Menu.Item>
                                    {({active}) => (
                                        <button
                                            type="button"
                                            onClick={toggleEditMode}
                                            className={classNames(
                                                active ? 'bg-gray-100' : '',
                                                'block w-full px-3 py-1.5 text-left text-xs text-gray-700'
                                            )}
                                        >
                                            Edit
                                        </button>
                                    )}
                                </Menu.Item>
                                <Menu.Item disabled={isRecalculating}>
                                    {({active, disabled}) => (
                                        <button
                                            type="button"
                                            disabled={disabled}
                                            onClick={handleRecalculateBalance}
                                            className={classNames(
                                                active && !disabled ? 'bg-gray-100' : '',
                                                disabled ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700',
                                                'block w-full px-3 py-1.5 text-left text-xs'
                                            )}
                                        >
                                            Recalculate Balance
                                        </button>
                                    )}
                                </Menu.Item>
                            </Menu.Items>
                        </Transition>
                    </Menu>
                </td>
            </Transition>
            <Transition
                show={editMode}
                as="tr"
                enter="transition-opacity duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                className={!editMode ? "absolute left-1/2" : "relative z-20 focus-within:z-50"}
            >
                <td colSpan={7} className={!editMode ? "relative right-1/2" : "relative"}>
                    <EditAccountForm account={account} close={toggleEditMode}/>
                </td>
            </Transition>
        </>
    );
};

export default AccountItem;
